import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import SettingsPanel from './SettingsPanel'; // Make sure path is correct
import ToggleSwitch from './ToggleSwitch';
import CustomSelect from './CustomSelect.jsx';
import {
    setShowOnlyFixes,
    setBackupOriginalFiles,
    setFixIncompatibleFiles,
    setConvertAiffToWav,
    setConversionFormat,
    setActiveFilter,
    setFileTypeFilter,
    setUseStatusBadges
} from '../redux/slices/fixSettingsSlice';
import {ReactComponent as AudioFileIcon } from '../assets/icons/audio-file.svg';
import { FLAC_DEPENDS_SUMMARY } from '../constants/flacCompatibility';

const FixSettings = () => {
    const dispatch = useDispatch();
    const activeFilters = useSelector((state) => state.fixSettings.activeFilters);
    const activeFileTypes = useSelector((state) => state.fixSettings.activeFileTypes);
    const convertAiffToWav = useSelector((state) => state.fixSettings.convertAiffToWav);
    const conversionFormat = useSelector((state) => state.fixSettings.conversionFormat);

    const filterSection = (
        <>
            <div className="filter-container">
                <button
                    className={`filter-btn compatible ${activeFilters.includes('compatible') ? 'active' : ''}`}
                    onClick={() => dispatch(setActiveFilter('compatible'))}
                    title="Files that can be fixed"
                >
                    Compatible
                </button>
                <button
                    className={`filter-btn incompatible ${activeFilters.includes('incompatible') ? 'active' : ''}`}
                    onClick={() => dispatch(setActiveFilter('incompatible'))}
                    title="Files that cannot be fixed"
                >
                    Incompatible
                </button>
                <button
                    className={`filter-btn fixed ${activeFilters.includes('fixed') ? 'active' : ''}`}
                    onClick={() => dispatch(setActiveFilter('fixed'))}
                    title="Files that have been fixed"
                >
                    Fixed
                </button>
                <button
                    className={`filter-btn converted ${activeFilters.includes('converted') ? 'active' : ''}`}
                    onClick={() => dispatch(setActiveFilter('converted'))}
                    title="Files converted to another format"
                >
                    Converted
                </button>
                <button
                    className={`filter-btn depends ${activeFilters.includes('depends') ? 'active' : ''}`}
                    onClick={() => dispatch(setActiveFilter('depends'))}
                    title={FLAC_DEPENDS_SUMMARY}
                >
                    Depends
                </button>
            </div>
            <div className="filter-container file-types-container">
                <button
                    className={`filter-btn file-type wav ${activeFileTypes.includes('wav') ? 'active' : ''}`}
                    onClick={() => dispatch(setFileTypeFilter('wav'))}
                    title="WAV files"
                >
                    <AudioFileIcon className="filter-icon" />
                    WAV
                </button>
                <button
                    className={`filter-btn file-type mp3 ${activeFileTypes.includes('mp3') ? 'active' : ''}`}
                    onClick={() => dispatch(setFileTypeFilter('mp3'))}
                    title="MP3 files"
                >
                    <AudioFileIcon className="filter-icon" />
                    MP3
                </button>
                <button
                    className={`filter-btn file-type aiff ${activeFileTypes.includes('aiff') ? 'active' : ''}`}
                    onClick={() => dispatch(setFileTypeFilter('aiff'))}
                    title="AIFF files"
                >
                    <AudioFileIcon className="filter-icon" />
                    AIFF
                </button>
                <button
                    className={`filter-btn file-type flac ${activeFileTypes.includes('flac') ? 'active' : ''}`}
                    onClick={() => dispatch(setFileTypeFilter('flac'))}
                    title="FLAC files"
                >
                    <AudioFileIcon className="filter-icon" />
                    FLAC
                </button>
                <button
                    className={`filter-btn file-type ${activeFileTypes.includes('others') ? 'active' : ''}`}
                    onClick={() => dispatch(setFileTypeFilter('others'))}
                    title="Other audio files"
                >
                    <AudioFileIcon className="filter-icon" />
                    Others
                </button>
            </div>
        </>
    );

    return (
        <SettingsPanel
            filtersComponent={filterSection}
            settings={[
                {
                    id: 'fixIncompatibleFiles',
                    selector: 'fixIncompatibleFiles',
                    label: 'Fix Incompatible WAV Files',
                    action: setFixIncompatibleFiles
                },
                {
                    id: 'convertFlacFiles',
                    customComponent: (
                        <div key="convertFlacFiles" className="loudness-label-tooltip-container" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <ToggleSwitch
                                id="convertAiffToWav"
                                checked={convertAiffToWav}
                                onChange={(value) => dispatch(setConvertAiffToWav(value))}
                                label="Convert FLAC files"
                            />
                            <div style={{
                                opacity: convertAiffToWav ? 1 : 0.5,
                                pointerEvents: convertAiffToWav ? 'auto' : 'none',
                                marginTop: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ fontSize: '14px', color: '#ddd' }}>To:</span>
                                <div style={{ width: '100px' }}>
                                    <CustomSelect
                                        value={conversionFormat}
                                        onChange={(e) => dispatch(setConversionFormat(e.target.value))}
                                        className="organizer-select compact-select"
                                        options={[
                                            { value: 'wav', label: 'WAV' },
                                            { value: 'mp3', label: 'MP3' },
                                            { value: 'aiff', label: 'AIFF' }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    )
                },
                {
                    id: 'backupOriginalFiles',
                    selector: 'backupOriginalFiles',
                    label: 'Keep Original Files Untouched',
                    tooltip: 'On: saves a "[fixed]" copy next to each original. Off: fixes the file in place.',
                    action: setBackupOriginalFiles
                },
                {
                    id: 'useStatusBadges',
                    selector: 'useStatusBadges',
                    label: 'Show Status as Text Badges',
                    action: setUseStatusBadges
                }
            ]}
            sliceSelector={(state) => state.fixSettings}
        />
    );
};

export default FixSettings;
